import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import PrimaryButton from "../../Components/Buttons/primary-button.js";
import OutlinedButton from "../../Components/Buttons/outline-button.js";
import CustomRadioButton from "../../Components/Form Fields/radio-button.js";

import renderAddStripeCardForm from "../../Organisms/Modals/Cards/add-stripe-card-form.js";
import renderStatusModal from "../../Organisms/Modals/status-modal.js";

import { useConsumerPaymentMethods } from "../../CustomHooks/Queries/index.js";
import { useUpdateConsumerDealPaymentMethod } from "../../CustomHooks/Mutations/index.js";

import { useModalContext } from "../../Contexts/ModalContext";

import { dispatchErrorToast, formatCurrency, getUserSfid, zPad } from "../../Utils/helpers.js";
import { APIQueryKeys } from "../../Utils/constants.js";

import { DebitCards as DebitCardsImage } from "../../Assets/Images/";
import { ArrowLeftCircle, AddCircleFilled, Wallet, CardFilled, CheckCircleWithShadow } from "../../Assets/Icons";

import "./payment-methods.scss";

const ConsumerPaymentMethod = ({ paymentMethod, selectedMethod, selectable = false, onSelect }) => {
  const selectCardForPayment = () => {
    if (selectable) {
      onSelect?.(paymentMethod);
    }
  };

  const isWalletOrStakeChecking = paymentMethod?.is_stake_checking || paymentMethod?.is_wallet;

  const isSelected =
    ((selectedMethod?.is_stake_checking || selectedMethod?.is_wallet) && isWalletOrStakeChecking) ||
    paymentMethod?.card_id === selectedMethod?.card_id;

  const PaymentMethodIcon = isWalletOrStakeChecking ? Wallet : CardFilled;

  return (
    <div
      className={`payment-method-item${isSelected ? " selected" : ""}${selectable ? " selectable" : ""}`}
      onClick={selectCardForPayment}
    >
      <div className="payment-method-icon-wrapper">
        <PaymentMethodIcon className="payment-method-logo" />
      </div>
      <div className="payment-method-info">
        <div className="payment-method-name">
          {isWalletOrStakeChecking
            ? `Stake ${paymentMethod?.is_stake_checking ? "Checking" : "Wallet"}`
            : paymentMethod?.card_type}
        </div>
        <div className="payment-method-detail">
          {isWalletOrStakeChecking
            ? formatCurrency(paymentMethod?.current_balance ?? 0)
            : `••••${paymentMethod?.card_last_4_digits}, exp ${zPad(paymentMethod?.exp_month, 2)}/${zPad(
                paymentMethod?.exp_year % 100,
                2
              )}`}
        </div>
      </div>
      <div className="actions">
        {selectable && <CustomRadioButton checked={isSelected} onChange={selectCardForPayment} />}
      </div>
    </div>
  );
};

export default function ConsumerPaymentMethodsPage() {
  return (
    <div className="consumer-payment-methods">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <ConsumerPaymentMethods />
          </div>
        </div>
      </section>
    </div>
  );
}

export function ConsumerPaymentMethods({
  presentedForSelection = false,
  selectedPaymentMethod,
  onChange,
  mustUpdatePaymentMethod = false,
  chargePaymentImmediately = false,
  isBackupPayment = false,
  dealId,
}) {
  const [selectedMethod, setSelectedMethod] = useState(selectedPaymentMethod);
  const previousPaymentMethodsLengthRef = useRef(0);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { isLoading: isLoadingPaymentMethods, data: paymentMethods } = useConsumerPaymentMethods();

  const { isLoading: isUpdatingConsumerDealPaymentMethod, mutate: UpdateConsumerDealPaymentMethod } =
    useUpdateConsumerDealPaymentMethod({
      onSuccess: ({ data }) => {
        queryClient.refetchQueries(APIQueryKeys.consumerDeals);
        renderStatusModal(modalContext, {
          StatusIcon: CheckCircleWithShadow,
          title: "Payment Method Updated",
          description: data?.data?.description,
          CTAButtonText: "Done",
          onCTAButtonClick: onModalClose,
        });
      },
      onError: (err) => dispatchErrorToast(err?.response?.data?.error, { key: "updateConsumerDealPaymentMethod" }),
    });

  const availablePaymentMethods = useMemo(
    () =>
      (paymentMethods?.data?.data ?? []).filter(
        (paymentMethod) => !isBackupPayment || !(paymentMethod?.is_stake_checking || paymentMethod?.is_wallet)
      ),
    [paymentMethods, isBackupPayment]
  );

  // Auto-select newly added card when it appears
  useEffect(() => {
    if (
      presentedForSelection &&
      availablePaymentMethods.length > previousPaymentMethodsLengthRef.current &&
      previousPaymentMethodsLengthRef.current === 0 &&
      !selectedMethod
    ) {
      // Card was just added - auto-select it
      const newestCard = availablePaymentMethods[0];
      setSelectedMethod(newestCard);
    }
    previousPaymentMethodsLengthRef.current = availablePaymentMethods.length;
  }, [presentedForSelection, availablePaymentMethods.length, availablePaymentMethods, selectedMethod]);

  const connectedPaymentMethods = useMemo(() => {
    return availablePaymentMethods.map((paymentMethod, idx) => (
      <ConsumerPaymentMethod
        key={idx}
        paymentMethod={paymentMethod}
        selectable={presentedForSelection}
        selectedMethod={selectedMethod}
        onSelect={(selectedMethod) => setSelectedMethod(selectedMethod)}
      />
    ));
  }, [presentedForSelection, availablePaymentMethods, selectedMethod]);

  const handleConfirmChange = () => {
    if (mustUpdatePaymentMethod)
      UpdateConsumerDealPaymentMethod({
        id: dealId,
        charge_fees_immediately: chargePaymentImmediately,
        sfid: getUserSfid(),
        payment_details: {
          payment_method: selectedMethod?.is_wallet
            ? "Wallet"
            : selectedMethod?.is_stake_checking
            ? "Stake Checking"
            : "Card",
          ...(selectedMethod?.is_card
            ? {
                card_id: selectedMethod?.card_id,
              }
            : {}),
        },
        is_backup_card: isBackupPayment,
      });
    else onChange(selectedMethod);
  };

  const handleAddButtonClick = () =>
    renderAddStripeCardForm(modalContext, {
      useStakeStripe: true,
      onCardAdded: () => {
        // Refetch payment methods after card is added
        queryClient.refetchQueries(APIQueryKeys.consumerPaymentMethods);
      },
    });

  return (
    <>
      {!presentedForSelection && (
        <div className="section-header">
          <button className="go-back-button" onClick={() => navigate(-1)}>
            <ArrowLeftCircle />
          </button>
          <h3 className="section-title">Payment Methods</h3>
          <div className="dummy" />
        </div>
      )}
      {isLoadingPaymentMethods ? (
        <div className="loading-content">
          <CircularProgress />
        </div>
      ) : availablePaymentMethods.length > 0 ? (
        <div className="content mt-3">
          <div className="top-container">
            {connectedPaymentMethods}
            <button className="add-account" onClick={handleAddButtonClick}>
              <AddCircleFilled />
              Add credit or debit card
            </button>
          </div>
          {presentedForSelection && (
            <div className="bottom-container stick-to-modal-bottom">
              <PrimaryButton onClick={handleConfirmChange} disabled={isUpdatingConsumerDealPaymentMethod}>
                {mustUpdatePaymentMethod ? "Update" : "Confirm"}
                {isUpdatingConsumerDealPaymentMethod && <CircularProgress />}
              </PrimaryButton>
            </div>
          )}
        </div>
      ) : (
        <div className={`no-payment-method-found ${presentedForSelection ? "mx-sm-6 mx-4" : ""}`}>
          <div className="card-icon mb-8">
            <DebitCardsImage />
          </div>
          <h2 className="no-payment-method-title mb-1">No payment method found</h2>
          <p className="no-payment-method-message mb-sm-6 mb-5">
            You don’t have any payment method connected to your account.
          </p>
          <OutlinedButton onClick={handleAddButtonClick}>Add credit or debit card</OutlinedButton>
        </div>
      )}
    </>
  );
}
